import React from "react";
import { Helmet } from "react-helmet-async";
import { PricingPlans } from "@/components/pricing/PricingPlans";
import { FeatureComparison } from "@/components/pricing/FeatureComparison";
import { PricingFAQ } from "@/components/pricing/PricingFAQ";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { HelpCircle, CreditCard, DollarSign, CheckCircle, Star, Users, MessageSquare, Calendar } from "lucide-react";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const Pricing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white relative">
      <Helmet>
        <title>Pricing - Hivetivity | Simple, Transparent Pricing</title>
        <meta 
          name="description" 
          content="Choose the perfect plan for your youth program. Start your 14-day free trial today - no credit card required. Transparent pricing with no hidden fees." 
        />
        <meta 
          name="keywords" 
          content="hivetivity pricing, youth program software pricing, activity center software cost, dance studio software price" 
        />
        <link rel="canonical" href="https://hivetivity.com/pricing" />
      </Helmet>
      
      {/* Top Navigation */}
      <TopNavbar />
      
      {/* Subtle background matching homepage */}
      <div className="absolute inset-0 z-0 opacity-20">
        <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-[#FA8C00] blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-[#4ba3c7] blur-3xl"></div>
      </div>
      
      {/* Main content with padding for navbar */}
      <div className="relative z-10 pt-16">
        {/* Hero Section */}
        <section className="py-6 md:py-10 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                Simple, transparent pricing that <span className="text-[#FA8C00] italic">grows with you</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                Start your 14-day free trial today - no credit card required. Choose the plan that fits your business size and unlock powerful features.
              </p>
              
              {/* Key Benefits */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-8">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex items-center gap-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                >
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700 font-medium">14-day free trial</span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex items-center gap-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                >
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700 font-medium">No setup fees</span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex items-center gap-2 bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                >
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700 font-medium">Cancel anytime</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Dynamic Pricing Plans */}
        <PricingPlans showContactSales={true} />

        {/* Feature Comparison Table */}
        <FeatureComparison />

        {/* FAQ Section */}
        <PricingFAQ />

        {/* Payment Processing Fees Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Payment Processing Fees
                </h2>
                <p className="text-lg text-gray-600">
                  No hidden fees - here's exactly what you pay for payment processing
                </p>
              </div>

              <TooltipProvider>
                <div className="grid gap-6 md:grid-cols-3">
                  {/* Service Fee */}
                  <Card className="hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-gray-200">
                    <CardContent className="p-6 text-center">
                      <div className="flex items-center justify-center mb-4">
                        <div className="p-3 bg-[#FA8C00]/10 rounded-full">
                          <DollarSign className="h-6 w-6 text-[#FA8C00]" />
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">Service Fee</h3>
                      <div className="text-2xl font-bold text-[#FA8C00] mb-2">1%</div>
                      <p className="text-gray-600 text-sm">Per paid invoice or at time of payment</p>
                    </CardContent>
                  </Card>

                  {/* Credit Card Fees */}
                  <Card className="hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-gray-200">
                    <CardContent className="p-6 text-center">
                      <div className="flex items-center justify-center mb-4">
                        <div className="p-3 bg-[#4ba3c7]/10 rounded-full">
                          <CreditCard className="h-6 w-6 text-[#4ba3c7]" />
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-800">Credit Card Fees</h3>
                      <div className="text-2xl font-bold text-[#4ba3c7] mb-2">2.9% + $0.30</div>
                      <p className="text-gray-600 text-sm">Per credit card transaction</p>
                    </CardContent>
                  </Card>

                  {/* eCheck Fees */}
                  <Card className="hover:shadow-lg hover:scale-[1.02] transition-all duration-300 border border-gray-200">
                    <CardContent className="p-6 text-center">
                      <div className="flex items-center justify-center mb-4">
                        <div className="p-3 bg-[#7ad4a0]/10 rounded-full">
                          <CheckCircle className="h-6 w-6 text-[#7ad4a0]" />
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold text-gray-800">eCheck Fees</h3>
                        <Tooltip>
                          <TooltipTrigger>
                            <HelpCircle className="h-4 w-4 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Allow your customers to pay with electronic check</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-2xl font-bold text-[#7ad4a0] mb-2">0.8% ($5.00 cap)</div>
                      <p className="text-gray-600 text-sm">Per eCheck transaction</p>
                    </CardContent>
                  </Card>
                </div>
              </TooltipProvider>

              <div className="text-center mt-8">
                <Badge variant="outline" className="text-sm px-4 py-2 bg-white">
                  Competitive rates with complete transparency
                </Badge>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Ready to get started?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Create your account to see our plans and start your 14-day free trial.
              </p>
              
              <Button 
                onClick={() => navigate("/business-trial")} 
                className="bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 hover:from-[#FA8C00]/90 hover:to-[#FA8C00]/80 text-white px-8 py-4 text-lg transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] rounded-xl shadow-lg"
              >
                Get Started - Free Trial
              </Button>
              
              <p className="text-sm text-gray-500 mt-4">
                No credit card required to start your free trial
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Pricing;
